type CelebrationTone = "success" | "favorite" | "complete";
type CelebrationPoint = { x: number; y: number };
type CelebrationOptions = {
  point?: CelebrationPoint;
  durationMs?: number;
  count?: number;
  /**
   * When set, the confetti host is appended to this element instead of
   * `document.body` and the origin is computed relative to the host.
   * Use this to anchor a celebration inside an overlay such as the
   * fullscreen video player so the burst stays inside its viewport.
   */
  host?: HTMLElement | null;
  /**
   * When true, the burst uses a larger count and a wider radius so
   * it reads as a "wall of confetti" rather than a click ripple.
   * Defaults to false.
   */
  burst?: boolean;
};

const COLORS: Record<CelebrationTone, string[]> = {
  success: ["#0071e3", "#5ac8fa", "#30d158", "#f5f5f7"],
  favorite: ["#fbbf24", "#ff9f0a", "#0071e3", "#ffffff"],
  complete: ["#2997ff", "#64d2ff", "#30d158", "#ffd166", "#ff9f0a", "#ffffff"],
};

let lastClickPoint: CelebrationPoint | null = null;

export function rememberCelebrationPoint(event: MouseEvent): void {
  lastClickPoint = { x: event.clientX, y: event.clientY };
}

export function celebrate(tone: CelebrationTone = "success", message?: string, options: CelebrationOptions = {}): void {
  if (typeof document === "undefined") return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  const hostEl = document.createElement("div");
  hostEl.className = "dmx-celebration";
  if (options.burst) hostEl.classList.add("dmx-celebration--burst");
  hostEl.setAttribute("aria-hidden", "true");
  const point = resolveOrigin(options);
  const durationMs = options.durationMs ?? 5000;
  const count = options.count ?? (options.burst ? 96 : 46);
  hostEl.style.setProperty("--origin-x", `${Math.round(point.x)}px`);
  hostEl.style.setProperty("--origin-y", `${Math.round(point.y)}px`);
  hostEl.style.setProperty("--celebration-duration", `${durationMs}ms`);
  const palette = COLORS[tone];
  const radius = options.burst ? 220 : 140;
  for (let i = 0; i < count; i += 1) {
    const bit = document.createElement("span");
    const angle = Math.random() * Math.PI * 2;
    const distance = (options.burst ? 0.4 : 0.6) * radius + Math.random() * radius;
    bit.style.setProperty("--x", `${Math.round(Math.cos(angle) * distance)}px`);
    bit.style.setProperty("--y", `${Math.round(Math.sin(angle) * distance - Math.random() * (options.burst ? 220 : 120))}px`);
    bit.style.setProperty("--r", `${Math.round(Math.random() * 540 - 180)}deg`);
    bit.style.setProperty("--delay", `${Math.round(Math.random() * (options.burst ? 720 : 420))}ms`);
    bit.style.setProperty("--w", `${Math.round((options.burst ? 7 : 5) + Math.random() * (options.burst ? 14 : 8))}px`);
    bit.style.setProperty("--h", `${Math.round((options.burst ? 10 : 6) + Math.random() * (options.burst ? 18 : 12))}px`);
    if (i % 5 === 0) bit.className = "is-dot";
    if (i % 7 === 0) bit.className = "is-spark";
    bit.style.background = palette[i % palette.length];
    hostEl.appendChild(bit);
  }
  if (message) {
    const label = document.createElement("strong");
    label.textContent = message;
    hostEl.appendChild(label);
  }
  const target = options.host ?? document.body;
  target.appendChild(hostEl);
  window.setTimeout(() => hostEl.remove(), durationMs + 800);
}

type FireworksOptions = {
  /** Total lifetime of the overlay before it is removed. */
  durationMs?: number;
  /** Host element; defaults to document.body for a true full-screen show. */
  host?: HTMLElement | null;
  /** Number of firework shells launched across the screen. */
  shells?: number;
  /** Optional centered caption (e.g. "Topic complete"). */
  message?: string;
};

const FIREWORK_PALETTE = [
  "#2997ff", "#64d2ff", "#30d158", "#ffd166",
  "#ff9f0a", "#ff453a", "#bf5af2", "#ffffff",
];

/**
 * Full-screen, screen-centered fireworks finale. Unlike `celebrate`
 * (a single confetti burst), this launches multiple shells staggered
 * across the whole viewport with a strong central burst — used when
 * the final video chapter completes.
 */
export function launchFireworks(options: FireworksOptions = {}): void {
  if (typeof document === "undefined") return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  const durationMs = options.durationMs ?? 6000;
  const host = options.host ?? document.body;
  const overlay = document.createElement("div");
  overlay.className = "dmx-fireworks";
  overlay.setAttribute("aria-hidden", "true");

  const spawnShell = (cxPct: number, cyPct: number, scale: number, delayMs: number): void => {
    const shell = document.createElement("div");
    shell.className = "dmx-fw-shell";
    shell.style.setProperty("--cx", `${cxPct}%`);
    shell.style.setProperty("--cy", `${cyPct}%`);
    const color = FIREWORK_PALETTE[Math.floor(Math.random() * FIREWORK_PALETTE.length)];
    const count = Math.round(26 * scale);
    const radius = 120 * scale;
    for (let i = 0; i < count; i += 1) {
      const bit = document.createElement("span");
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.22;
      const distance = radius * (0.6 + Math.random() * 0.5);
      bit.style.setProperty("--x", `${Math.round(Math.cos(angle) * distance)}px`);
      bit.style.setProperty("--y", `${Math.round(Math.sin(angle) * distance)}px`);
      bit.style.setProperty("--w", `${Math.round(4 + Math.random() * 4)}px`);
      bit.style.setProperty("--dur", `${Math.round(1100 + Math.random() * 700)}ms`);
      bit.style.setProperty("--delay", `${Math.round(delayMs + Math.random() * 120)}ms`);
      bit.style.color = i % 6 === 0 ? "#ffffff" : color;
      shell.appendChild(bit);
    }
    overlay.appendChild(shell);
  };

  // Strong central burst first, then a spread of shells across the
  // viewport so the show reads as full-screen fireworks.
  spawnShell(50, 46, 1.4, 0);
  const shells = options.shells ?? 7;
  for (let i = 0; i < shells; i += 1) {
    const cx = 14 + Math.random() * 72;
    const cy = 16 + Math.random() * 52;
    spawnShell(cx, cy, 0.7 + Math.random() * 0.6, 280 + i * 360 + Math.random() * 180);
  }

  if (options.message) {
    const label = document.createElement("strong");
    label.className = "dmx-fw-label";
    label.textContent = options.message;
    overlay.appendChild(label);
  }

  host.appendChild(overlay);
  window.setTimeout(() => overlay.remove(), durationMs + 1400);
}

type SideConfettiOptions = {
  /** Total lifetime of the overlay before it is removed. */
  durationMs?: number;
  /** Host element; defaults to document.body. */
  host?: HTMLElement | null;
  /** Optional centered caption. */
  message?: string;
};

const SIDE_PALETTE = ["#0071e3", "#5ac8fa", "#30d158", "#f5f5f7", "#bf5af2", "#ff9f0a"];

/**
 * GitHub-style side confetti: launches confetti from both bottom corners
 * of the screen, arcing upward and raining down over the center of the
 * viewport where the success modal sits. Unlike `celebrate` (single burst)
 * or `launchFireworks` (multi-shell explosive), this produces a gentle
 * shower effect from two cannons.
 */
export function launchSideConfetti(options: SideConfettiOptions = {}): void {
  if (typeof document === "undefined") return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  const durationMs = options.durationMs ?? 3200;
  const host = options.host ?? document.body;
  const overlay = document.createElement("div");
  overlay.className = "dmx-side-confetti";
  overlay.setAttribute("aria-hidden", "true");

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Reduced from 40 to 18 per cannon (36 total) to prevent jank.
  // The particles use GPU-composited transform/opacity only, but fewer
  // elements means fewer compositor layers and less layout pressure.
  const count = 18;
  const frag = document.createDocumentFragment();

  const spawnCannon = (originX: number, direction: 1 | -1): void => {
    for (let i = 0; i < count; i += 1) {
      const bit = document.createElement("span");
      // Angle: shoot upward and toward center, with spread
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.8 + direction * 0.35;
      const power = 0.55 + Math.random() * 0.45;
      const distance = power * Math.max(vw, 600) * 0.42;
      const xTravel = Math.cos(angle) * distance;
      const yTravel = Math.sin(angle) * distance;
      bit.style.setProperty("--ox", `${Math.round(originX)}px`);
      bit.style.setProperty("--oy", `${Math.round(vh * 0.88)}px`);
      bit.style.setProperty("--tx", `${Math.round(xTravel)}px`);
      bit.style.setProperty("--ty", `${Math.round(yTravel)}px`);
      bit.style.setProperty("--fall", `${Math.round(vh * 0.5 + Math.random() * vh * 0.3)}px`);
      bit.style.setProperty("--delay", `${Math.round(Math.random() * 500)}ms`);
      bit.style.setProperty("--dur", `${Math.round(1600 + Math.random() * 1000)}ms`);
      bit.style.setProperty("--w", `${Math.round(5 + Math.random() * 7)}px`);
      bit.style.setProperty("--h", `${Math.round(8 + Math.random() * 12)}px`);
      bit.style.setProperty("--r", `${Math.round(Math.random() * 720 - 360)}deg`);
      if (i % 5 === 0) bit.className = "is-dot";
      bit.style.background = SIDE_PALETTE[i % SIDE_PALETTE.length];
      frag.appendChild(bit);
    }
  };

  // Left cannon shoots rightward, right cannon shoots leftward
  spawnCannon(0, 1);
  spawnCannon(vw, -1);

  if (options.message) {
    const label = document.createElement("strong");
    label.textContent = options.message;
    frag.appendChild(label);
  }

  overlay.appendChild(frag);
  host.appendChild(overlay);
  window.setTimeout(() => overlay.remove(), durationMs + 800);
}

function resolveOrigin(options: CelebrationOptions): CelebrationPoint {
  if (options.point) return options.point;
  if (options.host && typeof options.host.getBoundingClientRect === "function") {
    const rect = options.host.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }
  if (lastClickPoint) return lastClickPoint;
  return { x: window.innerWidth / 2, y: window.innerHeight * 0.58 };
}
